import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  List,
  Typography,
  IconButton,
  Tooltip,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Badge,
  Dialog,
  Button,
  Fade,
  Slide,
  Chip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Search,
  EditNote,
  Close,
  Phone,
  ChevronRight,
  Add,
  DescriptionOutlined,
} from "@mui/icons-material";

// Animation transition for dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Sidebar = ({
  contacts,
  selectedId,
  onSelect,
  getInitials,
  formatTime,
  onStartNewChat,
  onCreateTemplate,
  onManageTemplates,
  newChatOpen,
  setNewChatOpen,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const primaryDark = theme.palette.primary.dark;

  const [query, setQuery] = useState("");
  const [newPhone, setNewPhone] = useState("");

  // Filter contacts based on search query
  const filtered = contacts.filter(
    (c) =>
      c.client_name?.toLowerCase().includes(query.toLowerCase()) ||
      c.phone?.includes(query) ||
      c.whatsapp?.phone?.includes(query)
  );

  // All saved contacts with names for the popup (show all, not just 3)
  const savedContacts = useMemo(() => {
    return contacts
      .filter((c) => c.client_name && c.client_name !== c.phone)
      .map((c) => ({
        id: c._id,
        name: c.client_name,
        phone: c.phone || c.whatsapp?.phone,
        initials: getInitials(c.client_name),
        contact: c,
      }));
  }, [contacts, getInitials]);

  // Search results for the popup - filter based on newPhone input
  const searchResults = useMemo(() => {
    if (!newPhone.trim()) return [];

    const searchTerm = newPhone.replace(/\s+/g, "").toLowerCase();

    // Check if input matches any existing contact (by name or phone)
    const matchingContacts = contacts.filter((c) => {
      const phone = (c.phone || c.whatsapp?.phone || "").replace(/\s+/g, "").toLowerCase();
      const name = (c.client_name || "").toLowerCase();
      return phone.includes(searchTerm) || name.includes(searchTerm);
    });

    const results = matchingContacts.slice(0, 5).map((c) => ({
      type: "existing",
      id: c._id,
      name: c.client_name,
      phone: c.phone || c.whatsapp?.phone,
      initials: getInitials(c.client_name || c.phone),
      contact: c,
    }));

    // Check if input looks like a phone number (has digits)
    const cleanedNumber = searchTerm.replace(/[^0-9+]/g, "");
    const isPhoneNumber = cleanedNumber.length >= 7;

    // If it looks like a phone number and no exact phone match exists, add "NEW NUMBER" option
    if (isPhoneNumber) {
      const exactPhoneMatch = contacts.some((c) => {
        const phone = (c.phone || c.whatsapp?.phone || "").replace(/\s+/g, "").replace(/[^0-9+]/g, "");
        return phone === cleanedNumber || phone.includes(cleanedNumber) || cleanedNumber.includes(phone);
      });

      if (!exactPhoneMatch) {
        results.unshift({
          type: "new",
          phone: newPhone.trim(),
          label: "Start a conversation with this number",
        });
      }
    }

    return results;
  }, [newPhone, contacts, getInitials]);

  const handleStartNewChat = () => {
    if (newPhone.trim()) {
      onStartNewChat(newPhone.trim());
      setNewChatOpen(false);
      setNewPhone("");
    }
  };

  const handleSelectContact = (contact) => {
    if (contact.contact) {
      onSelect(contact.contact);
    } else {
      const fullContact = contacts.find((c) => c._id === contact.id);
      if (fullContact) {
        onSelect(fullContact);
      }
    }
    setNewChatOpen(false);
    setNewPhone("");
  };

  const handleSelectSearchResult = (result) => {
    if (result.type === "existing") {
      onSelect(result.contact);
    } else {
      onStartNewChat(result.phone);
    }
    setNewChatOpen(false);
    setNewPhone("");
  };

  // Generate avatar colors based on name
  const getAvatarColor = (name) => {
    const colors = [
      primaryColor, // theme primary
      "#2563eb", // blue
      "#059669", // green
      "#dc2626", // red
      "#d97706", // amber
      "#ec4899", // pink
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        borderRight: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {/* ===== Header Section ===== */}
      <Box
        sx={{
          px: 2.5, // padding-x: 20px
          pt: 2, // padding-top: 16px
          pb: 2, // padding-bottom: 16px
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        {/* Title Row with Manage Templates Link and New Chat Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2, // margin-bottom: 16px
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: "24px",
              color: "#1e293b",
              letterSpacing: "-0.02em",
            }}
          >
            Messages
          </Typography>

          {/* Manage Templates Link + New Chat Icon in same row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 0.5,
              py: 0.5,
              borderRadius: "12px",
            }}
          >
            {/* Manage Templates Link */}
            <Box
              onClick={onManageTemplates}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                cursor: "pointer",
                px: 1.5,
                py: 0.75,
                borderRadius: "8px",
                border: `1px solid ${primaryColor}`,
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: primaryColor,
                  bgcolor: alpha(primaryColor, 0.04),
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: primaryColor,
                }}
              >
                Manage Templates
              </Typography>
            </Box>

            {/* Filter / Sort Icon */}
            <Tooltip title="New Message" arrow>
              <IconButton
                size="small"
                onClick={() => setNewChatOpen(true)}
                sx={{
                  color: "#64748b",
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                    color: primaryColor,
                  },
                }}
              >
                <EditNote sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Create New Template Button */}
        <Button
          fullWidth
          variant="contained"
          startIcon={<Add sx={{ fontSize: 20 }} />}
          onClick={onCreateTemplate}
          sx={{
            bgcolor: primaryColor,
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9375rem",
            py: 1.25,
            borderRadius: "12px",
            boxShadow: `0 4px 14px ${alpha(primaryColor, 0.35)}`,
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: primaryDark,
              boxShadow: `0 6px 20px ${alpha(primaryColor, 0.45)}`,
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          Create New Template
        </Button>
      </Box>

      {/* ===== Search Section ===== */}
      <Box
        sx={{
          px: 2.5,
          pt: 1.5,
          pb: 1,
        }}
      >

        {/* Search Input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: "#94a3b8", fontSize: 18 }} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: "#f8fafc",
              borderRadius: "10px",
              border: "none",
              height: 42, // 42px height
              fontSize: "0.875rem",
              transition: "all 0.2s ease",
              "& fieldset": { border: "none" },
              "&:hover": {
                bgcolor: "#f1f5f9",
              },
              "&.Mui-focused": {
                bgcolor: "#f1f5f9",
                boxShadow: `0 0 0 2px ${alpha(primaryColor, 0.1)}`,
              },
            },
          }}
        />
      </Box>

      {/* ===== Conversation List ===== */}
      <List
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2, // padding-x: 16px
          py: 0.5, // padding-y: 4px
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e2e8f0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#cbd5e1",
          },
        }}
      >
        {filtered.length === 0 ? (
          <Fade in timeout={300}>
            <Box
              sx={{
                height: "60%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                color: "#64748b",
                mt: 8,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Search sx={{ fontSize: 28, color: "#94a3b8" }} />
              </Box>
              <Typography fontWeight={600} fontSize="0.9375rem">
                No conversations found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Try a different name or number
              </Typography>
            </Box>
          </Fade>
        ) : (
          filtered.map((c, index) => {
            const isSelected = selectedId === c._id;
            const hasUnread = c.unread_count > 0;
            return (
              <Fade in timeout={150 + index * 50} key={c._id}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => onSelect(c)}
                  sx={{
                    borderRadius: "14px",
                    mb: 0.5, // margin-bottom: 4px
                    px: 1.25, // padding-x: 10px
                    py: 1, // padding-y: 8px
                    transition: "all 0.15s ease",
                    border: isSelected ? `2px solid ${primaryColor}` : "2px solid transparent",
                    bgcolor: isSelected ? alpha(primaryColor, 0.08) : "transparent",
                    "&:hover": {
                      bgcolor: isSelected ? alpha(primaryColor, 0.12) : "#f8fafc",
                    },
                    "&.Mui-selected": {
                      bgcolor: alpha(primaryColor, 0.08),
                      "&:hover": { bgcolor: alpha(primaryColor, 0.12) },
                    },
                  }}
                >
                  <ListItemAvatar>
                    <StyledBadge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      variant="dot"
                      invisible={!hasUnread}
                    >
                      <Avatar
                        sx={{
                          bgcolor: isSelected ? primaryColor : "#94a3b8", // Theme primary when selected, Grey when not
                          fontWeight: 600,
                          fontSize: "1rem", // 16px
                          width: 46, // 46px
                          height: 46, // 46px
                          transition: "all 0.15s ease",
                        }}
                      >
                        {getInitials(c.client_name || c.phone || "Unknown")}
                      </Avatar>
                    </StyledBadge>
                  </ListItemAvatar>

                  <ListItemText
                    primaryTypographyProps={{ component: "div" }}
                    secondaryTypographyProps={{ component: "div" }}
                    sx={{ ml: 0.75 }} // margin-left: 6px
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: c.unread_count > 0 ? 700 : 600,
                            color: "#1e293b",
                            fontSize: "0.9375rem", // 15px
                          }}
                        >
                          {c.client_name || c.phone || "No Name"}
                        </Typography>

                        <Typography
                          sx={{
                            color: "#94a3b8",
                            fontSize: "0.75rem", // 12px
                            fontWeight: 500,
                          }}
                        >
                          {formatTime(c.last_message_at || c.created_at)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 0.5, // margin-top: 4px
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            maxWidth: c.unread_count > 0 ? 170 : 200, // 170px when has badge
                            color: "#64748b",
                            fontSize: "0.8125rem", // 13px
                            fontWeight: 400,
                          }}
                        >
                          {c.last_message || "Click to start chatting"}
                        </Typography>

                        {/* WhatsApp-like Unread Badge */}
                        {c.unread_count > 0 && (
                          <UnreadBadge count={c.unread_count} color={primaryColor} />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </Fade>
            );
          })
        )}
      </List>

      {/* ===== New Chat Dialog - Matching Reference Design ===== */}
      <Dialog
        open={newChatOpen}
        onClose={() => {
          setNewChatOpen(false);
          setNewPhone("");
        }}
        TransitionComponent={Transition}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px", // border-radius: 20px
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
            maxWidth: 420, // max-width: 420px
          },
        }}
      >
        {/* Dialog Content */}
        <Box sx={{ p: 3 }}> {/* padding: 24px */}
          {/* Header with Title and Close Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3, // margin-bottom: 24px
            }}
          >
            <Typography
              sx={{
                fontSize: "1.25rem", // 20px
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              Start New Chat
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setNewChatOpen(false);
                setNewPhone("");
              }}
              sx={{
                color: "#94a3b8",
                width: 32,
                height: 32,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#f1f5f9",
                  color: "#64748b",
                },
              }}
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Search Label */}
          <Typography
            sx={{
              fontSize: "0.6875rem", // 11px
              fontWeight: 600,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              mb: 1, // margin-bottom: 8px
            }}
          >
            Search Lead or Enter Number
          </Typography>

          {/* Search Input */}
          <TextField
            autoFocus
            fullWidth
            placeholder="Enter name or mobile number..."
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                if (searchResults.length > 0) {
                  handleSelectSearchResult(searchResults[0]);
                } else if (newPhone.trim()) {
                  handleStartNewChat();
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#94a3b8", fontSize: 20 }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
                height: 48, // 48px height
                px: 1, // padding-x: 8px
                transition: "all 0.2s ease",
                "& fieldset": { border: "none" },
                "&:hover": {
                  borderColor: "#cbd5e1",
                },
                "&.Mui-focused": {
                  borderColor: primaryColor,
                  boxShadow: `0 0 0 3px ${alpha(primaryColor, 0.1)}`,
                },
              },
            }}
          />

          {/* Search Results Section - Show when searching */}
          {newPhone.trim() && searchResults.length > 0 && (
            <Fade in timeout={200}>
              <Box sx={{ mt: 3 }}> {/* margin-top: 24px */}
                <Typography
                  sx={{
                    fontSize: "0.6875rem", // 11px
                    fontWeight: 600,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: 1.5, // margin-bottom: 12px
                  }}
                >
                  Search Results
                </Typography>

                <Box
                  sx={{
                    maxHeight: 280,
                    overflowY: "auto",
                    overflowX: "hidden",
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#e2e8f0",
                      borderRadius: "10px",
                    },
                  }}
                >
                  {searchResults.map((result, index) => (
                    <Box
                      key={index}
                      onClick={() => handleSelectSearchResult(result)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5, // padding: 12px
                        borderRadius: "12px",
                        bgcolor: result.type === "new" ? alpha(primaryColor, 0.08) : "transparent",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        mb: 0.5,
                        "&:hover": {
                          bgcolor: result.type === "new" ? alpha(primaryColor, 0.12) : "#f8fafc",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      {/* Avatar/Icon */}
                      {result.type === "new" ? (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: primaryColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1.5, // margin-right: 12px
                          }}
                        >
                          <Phone sx={{ color: "#fff", fontSize: 20 }} />
                        </Box>
                      ) : (
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: getAvatarColor(result.name || result.phone),
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            mr: 1.5,
                          }}
                        >
                          {result.initials}
                        </Avatar>
                      )}

                      {/* Contact Info */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.9375rem", // 15px
                              color: "#1e293b",
                            }}
                          >
                            {result.type === "new" ? result.phone : result.name || result.phone}
                          </Typography>
                          {result.type === "new" && (
                            <Chip
                              label="NEW NUMBER"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.625rem", // 10px
                                fontWeight: 700,
                                bgcolor: "#10b981",
                                color: "#fff",
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem", // 13px
                            color: "#64748b",
                            mt: 0.25,
                          }}
                        >
                          {result.type === "new" ? result.label : result.phone}
                        </Typography>
                      </Box>

                      {/* Arrow Icon */}
                      <ChevronRight sx={{ color: "#94a3b8", fontSize: 20 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Recent Contacts Section - Show when no search (ALL saved contacts) */}
          {!newPhone.trim() && savedContacts.length > 0 && (
            <Fade in timeout={200}>
              <Box sx={{ mt: 3 }}> {/* margin-top: 24px */}
                <Typography
                  sx={{
                    fontSize: "0.6875rem", // 11px
                    fontWeight: 600,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    mb: 1.5, // margin-bottom: 12px
                  }}
                >
                  Recent Contacts
                </Typography>

                <Box
                  sx={{
                    maxHeight: 280,
                    overflowY: "auto",
                    overflowX: "hidden",
                    "&::-webkit-scrollbar": { width: "4px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#e2e8f0",
                      borderRadius: "10px",
                    },
                  }}
                >
                  {savedContacts.map((contact) => (
                    <Box
                      key={contact.id}
                      onClick={() => handleSelectContact(contact)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 1.25, // padding-y: 10px
                        px: 0.5, // padding-x: 4px
                        borderRadius: "10px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: "#f8fafc",
                          transform: "translateX(4px)",
                        },
                      }}
                    >
                      {/* Avatar with Initials */}
                      <Avatar
                        sx={{
                          width: 40, // 40px
                          height: 40, // 40px
                          bgcolor: getAvatarColor(contact.name),
                          fontSize: "0.875rem", // 14px
                          fontWeight: 600,
                          mr: 1.5, // margin-right: 12px
                        }}
                      >
                        {contact.initials}
                      </Avatar>

                      {/* Name and Phone */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.9375rem", // 15px
                            color: "#1e293b",
                          }}
                        >
                          {contact.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.8125rem", // 13px
                            color: "#64748b",
                          }}
                        >
                          {contact.phone}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Start Chat Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleStartNewChat}
            disabled={!newPhone.trim()}
            sx={{
              mt: 3, // margin-top: 24px
              bgcolor: primaryColor,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9375rem", // 15px
              py: 1.5, // padding-y: 12px
              borderRadius: "12px",
              boxShadow: `0 4px 14px ${alpha(primaryColor, 0.35)}`,
              transition: "all 0.3s ease",
              "&:hover": {
                bgcolor: primaryDark,
                boxShadow: `0 6px 20px ${alpha(primaryColor, 0.45)}`,
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
              "&.Mui-disabled": {
                bgcolor: "#e2e8f0",
                color: "#94a3b8",
                boxShadow: "none",
              },
            }}
          >
            Start Chat
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

// Unread Badge Component - Simple circular badge
const UnreadBadge = ({ count, color }) => {
  return (
    <Box
      sx={{
        minWidth: 22,
        height: 22,
        borderRadius: "11px",
        bgcolor: color || "#8b5cf6",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.75rem", // 12px
        fontWeight: 600,
        px: count > 9 ? 0.75 : 0,
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
};

// Styled Badge Component for Online Status - Simple green dot
const StyledBadge = ({ children, ...props }) => (
  <Badge
    {...props}
    sx={{
      "& .MuiBadge-badge": {
        backgroundColor: "#22c55e", // Green dot
        color: "#22c55e",
        boxShadow: "0 0 0 2px #fff",
        width: 10, // 10px
        height: 10, // 10px
        borderRadius: "50%",
        minWidth: 10,
      },
    }}
  >
    {children}
  </Badge>
);

export default Sidebar;
